import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, FileText, Wallet, Trash2, Eye, Edit } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select'; 
import InvoiceModal from '../components/modals/InvoiceModal';
import PaymentModal from '../components/modals/PaymentModal';
import CreditNoteModal from '../components/modals/CreditNoteModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import EntityModal from '../components/modals/EntityModal';
import { useAppStore } from '../store';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';

const SupplierAccount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<'all' | 'A' | 'B' | 'C' | 'X'>('all');
  const [selectedTransactionType, setSelectedTransactionType] = useState<'all' | 'invoice' | 'payment'>('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: 'invoice' | 'payment' } | null>(null);
  const [deletedItems, setDeletedItems] = useState<Set<string>>(new Set());
  
  const { suppliers, invoices, payments, deleteInvoice, deletePayment, calculateEntityBalance, updateSupplier } = useAppStore();
  
  const supplier = suppliers.find(s => s.id === id);
  
  const currentBalance = useMemo(() => {
    if (!id) return 0;
    return calculateEntityBalance(id, 'supplier');
  }, [id, calculateEntityBalance, invoices, payments]);
  
  const isFromProductionManager = (invoice: any): boolean => {
    return invoice.items && invoice.items.some((item: any) => 
      item.description && item.description.includes('ProductionManager')
    );
  };
  
  const supplierTransactions = useMemo(() => {
    if (!id) return [];
    
    // Get all payments for this supplier, including those not associated with invoices
    const supplierPayments = payments.filter(payment => {
      if (payment.invoice_id) {
        // Payment associated with an invoice
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        return invoice?.entity_id === id && invoice?.entity_type === 'supplier' && !payment.voided;
      } else if (payment.reference === `supplier:${id}` && !payment.voided) {
        // Account payment for this supplier
        return true;
      }
      return false;
    });
    
    return [
      ...invoices
        .filter(inv => inv.entity_id === id && inv.entity_type === 'supplier' && !inv.voided)
        .map(inv => ({
          id: inv.id,
          date: inv.date,
          timestamp: new Date(inv.date).getTime(),
          createdAt: new Date(inv.created_at).getTime(),
          description: `Factura ${inv.number}${isFromProductionManager(inv) ? ' (ProductionManager)' : ''}`,
          type: 'invoice' as const,
          invoiceType: inv.type,
          amount: inv.total,
          voided: Boolean(inv.voided),
          document: () => generateInvoicePDF(inv, supplier?.name || 'Proveedor'),
        })),
      ...supplierPayments
        .map(pay => {
          const invoice = pay.invoice_id ? invoices.find(inv => inv.id === pay.invoice_id) : null;
          
          const description = pay.invoice_id 
            ? (pay.invoice_type === 'credit_note' 
                ? `Nota de crédito ${pay.reference || ''}` 
                : `Pago ${pay.method}${pay.reference ? ` (${pay.reference})` : ''}`)
            : `Pago a cuenta ${pay.method}${pay.reference ? ` (${pay.reference})` : ''}`;
          
          return {
            id: pay.id,
            date: pay.date,
            timestamp: new Date(pay.date).getTime(),
            createdAt: new Date(pay.created_at).getTime(),
            description,
            type: 'payment' as const,
            invoiceType: pay.invoice_type,
            amount: pay.amount,
            voided: Boolean(pay.voided),
          };
        }),
    ];
  }, [id, invoices, payments]);
  
  const filteredTransactions = useMemo(() => {
    return supplierTransactions.filter(t => {
      const transactionDate = new Date(t.date);
      
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59);
        return transactionDate >= start && transactionDate <= end;
      }
      
      if (period !== 'all') {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstDayOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
        
        switch (period) {
          case 'month':
            return transactionDate >= firstDayOfMonth;
          case 'quarter':
            return transactionDate >= firstDayOfQuarter;
          case 'year':
            return transactionDate >= firstDayOfYear;
          default:
            return true;
        }
      }

      return true;
    }).filter(t => {
      if (selectedTransactionType !== 'all') {
        return t.type === selectedTransactionType;
      }
      return true;
    }).filter(t => {
      if (selectedInvoiceType !== 'all' && t.type === 'invoice') {
        return t.invoiceType === selectedInvoiceType;
      }
      return true;
    }).filter(t => !deletedItems.has(t.id));
  }, [supplierTransactions, period, startDate, endDate, selectedTransactionType, selectedInvoiceType, deletedItems]);
  
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      const dateComparison = b.timestamp - a.timestamp;
      if (dateComparison !== 0) return dateComparison;
      
      return b.createdAt - a.createdAt;
    });
  }, [filteredTransactions]);
  
  const totalBilling = useMemo(() => {
    return filteredTransactions
      .filter(t => t.type === 'invoice')
      .reduce((sum, t) => sum + t.amount, 0);
  }, [filteredTransactions]);
  
  const totalPayments = useMemo(() => {
    return filteredTransactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + t.amount, 0);
  }, [filteredTransactions]);

  const transactionsWithBalances = useMemo(() => {
    const chronologicalTransactions = [...filteredTransactions].sort((a, b) => {
      const dateComparison = a.timestamp - b.timestamp;
      if (dateComparison !== 0) return dateComparison;
      
      return a.createdAt - b.createdAt;
    });
    
    let runningBalance = 0;
    const withBalances = chronologicalTransactions.map(transaction => {
      if (transaction.type === 'invoice') {
        runningBalance += transaction.amount;
      } else if (transaction.invoiceType === 'credit_note') {
        runningBalance -= transaction.amount; // Credit notes decrease the balance (reduce what we owe)
      } else {
        runningBalance -= transaction.amount;
      }
      
      return {
        ...transaction,
        runningBalance
      };
    });
    
    return withBalances.sort((a, b) => {
      const dateComparison = b.timestamp - a.timestamp;
      if (dateComparison !== 0) return dateComparison;
      
      return b.createdAt - a.createdAt;
    });
  }, [filteredTransactions]);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'invoice') {
        await deleteInvoice(itemToDelete.id);
      } else {
        await deletePayment(itemToDelete.id);
      }
      
      setDeletedItems(prev => new Set([...prev, itemToDelete.id]));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  if (!supplier) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-12rem)]">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Proveedor no encontrado</h2>
          <p className="text-gray-600 dark:text-gray-400">El proveedor que buscas no existe o no tienes acceso.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{supplier.name}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{supplier.email}</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            icon={<Edit className="h-4 w-4" />}
            onClick={() => setIsEditModalOpen(true)}
          >
            Editar Proveedor
          </Button>
          <Button 
            variant="outline" 
            icon={<FileText className="h-4 w-4" />}
            onClick={() => setIsInvoiceModalOpen(true)}
          >
            Nueva Compra
          </Button>
          <Button 
            variant="outline" 
            icon={<Wallet className="h-4 w-4" />}
            onClick={() => setIsPaymentModalOpen(true)}
          >
            Registrar Pago
          </Button>
          <Button 
            variant="outline" 
            icon={<FileText className="h-4 w-4" />}
            onClick={() => setIsCreditNoteModalOpen(true)}
          >
            Nota de Crédito
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Compras Totales</p>
                <p className="mt-2 text-2xl font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(totalBilling)}
                </p>
              </div>
              <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
                <ArrowUpRight className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pagos Realizados</p>
                <p className="mt-2 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(totalPayments)}
                </p>
              </div>
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                <ArrowDownRight className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Saldo Pendiente</p>
                <p className={`mt-2 text-2xl font-semibold ${
                  currentBalance > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
                }`}>
                  {formatCurrency(currentBalance)}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${
                currentBalance > 0 ? 'bg-red-50 dark:bg-red-900/30' : 'bg-emerald-50 dark:bg-emerald-900/30'
              }`}>
                {currentBalance > 0 ? (
                  <ArrowUpRight className="h-6 w-6 text-red-600 dark:text-red-400" />
                ) : (
                  <ArrowDownRight className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader title="Movimientos de Cuenta" />
        <CardContent>
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Select
              label="Período"
              value={period}
              onChange={(value) => {
                setPeriod(value);
                setStartDate('');
                setEndDate('');
              }}
              options={[
                { value: 'all', label: 'Todo' },
                { value: 'month', label: 'Este mes' },
                { value: 'quarter', label: 'Este trimestre' },
                { value: 'year', label: 'Este año' },
                { value: 'custom', label: 'Personalizado' },
              ]}
            />
            
            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Desde
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                  />
                </div>
              </>
            )}

            <Select
              label="Tipo de Factura"
              value={selectedInvoiceType}
              onChange={(value) => setSelectedInvoiceType(value as typeof selectedInvoiceType)}
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'A', label: 'Factura A' },
                { value: 'B', label: 'Factura B' },
                { value: 'C', label: 'Factura C' },
                { value: 'X', label: 'Factura X' },
              ]}
            />

            <Select
              label="Tipo de Movimiento"
              value={selectedTransactionType}
              onChange={(value) => setSelectedTransactionType(value as typeof selectedTransactionType)}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'invoice', label: 'Facturas' },
                { value: 'payment', label: 'Pagos' },
              ]}
            />
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Vencimiento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Saldo
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {transactionsWithBalances.length > 0 ? (
                  transactionsWithBalances.map((transaction) => (
                    <tr 
                      key={transaction.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                        transaction.voided ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatArgentinaDate(transaction.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {transaction.type === 'invoice' && transaction.due_date ? 
                          formatArgentinaDate(transaction.due_date) : 
                          '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {transaction.description}
                        {transaction.voided && (
                          <span className="ml-2 text-red-600 dark:text-red-400 text-xs">(Anulado)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            transaction.voided
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : transaction.type === 'invoice'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          }`}
                        >
                          {transaction.voided
                            ? 'Anulado'
                            : transaction.type === 'invoice'
                            ? 'Compra'
                            : 'Pago'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <span
                          className={
                            transaction.type === 'payment' && transaction.invoiceType !== 'credit_note'
                              ? 'text-emerald-600 dark:text-emerald-400' 
                            : transaction.type === 'payment' && transaction.invoiceType === 'credit_note'
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }
                        >
                          {transaction.type === 'payment' && transaction.invoiceType !== 'credit_note' && '-'}
                          {transaction.type === 'payment' && transaction.invoiceType === 'credit_note' && '-'}
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <span className={transaction.runningBalance > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}>
                          {formatCurrency(transaction.runningBalance)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <div className="flex justify-center gap-2">
                          {transaction.type === 'invoice' && transaction.invoice_file && (
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Eye className="h-4 w-4" />}
                              onClick={() => window.open(transaction.invoice_file, '_blank')}
                            >
                              Ver Factura
                            </Button>
                          )}
                          
                          {!transaction.voided && (
                            <Button
                              variant="danger"
                              size="sm"
                              icon={<Trash2 className="h-4 w-4" />}
                              onClick={() => {
                                setItemToDelete({
                                  id: transaction.id,
                                  type: transaction.type
                                });
                                setDeleteModalOpen(true);
                              }}
                            >
                              Eliminar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      No hay movimientos para mostrar con los filtros seleccionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <InvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        entity_id={supplier.id}
        entity_type="supplier"
        entityName={supplier.name}
      />
      
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        entityId={supplier.id}
        entityName={supplier.name}
        entityType="supplier"
      />
      
      <CreditNoteModal
        isOpen={isCreditNoteModalOpen}
        onClose={() => setIsCreditNoteModalOpen(false)}
        entityId={supplier.id}
        entityName={supplier.name}
        entityType="supplier"
      />

      <EntityModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        entityType="supplier"
        onSave={async (updatedSupplier) => {
          if (id) {
            await updateSupplier(id, updatedSupplier);
          }
          setIsEditModalOpen(false);
        }}
        initialData={supplier}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title={`Eliminar ${itemToDelete?.type === 'invoice' ? 'Factura' : 'Pago'}`}
        message={`¿Estás seguro de que deseas eliminar ${itemToDelete?.type === 'invoice' ? 'esta factura' : 'este pago'}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  );
};

export default SupplierAccount;